import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Button, Flex, Image, Link, Text } from "@chakra-ui/react";
import React from "react";
import { RiLinkUnlink } from "react-icons/ri";
import { ConnectionTag } from "src/components/ConnectionTag";
import { disconnectStripeAccount } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { useInvalidateOrgConfig } from "src/hooks/useOrgConfig";
import stripeIcon from "src/images/stripe-icon.svg";
import { useEnv } from "src/services/env";
import { sleep } from "src/services/sleep";

type ConnectionStatus = "connected" | "not_connected";

type Props = {
  status: ConnectionStatus;
  stripeAccountId?: string;
  connectedDate?: string;
  stripeUrl?: string;
};

export const StripeConnectionCard: React.FC<Props> = ({
  status,
  stripeAccountId,
  connectedDate,
  stripeUrl,
}) => {
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Disconnect Stripe?",
    body: (
      <Flex direction="column" gap={4}>
        <Text>
          Disconnecting Stripe means{" "}
          <Text as="span" fontWeight="bold">
            forms that require payments
          </Text>{" "}
          will stop accepting charges.
        </Text>
        <Text>
          This cannot be undone or recovered, but you can always connect a
          Stripe account again.
        </Text>
      </Flex>
    ),
    confirmButton: {
      label: "Yes, disconnect",
    },
    cancelButton: {
      label: "No, cancel",
    },
  });
  const organization = useOrganization();
  const env = useEnv();
  const toast = useAvelaToast();

  const [isConnecting, setIsConnecting] = React.useState(false);
  const { invalidateOrgConfig } = useInvalidateOrgConfig();
  const handleConnect = React.useCallback(async () => {
    const org = organization.toNullable();
    if (!org) {
      console.error("Organization not available for Stripe OAuth");
      return;
    }
    setIsConnecting(true);
    const csrfToken = crypto.randomUUID();
    sessionStorage.setItem("stripe_oauth_state", csrfToken);
    const statePayload = {
      orgPath: org.path,
      organizationId: org.id,
      token: csrfToken,
    };
    const encodedState = btoa(JSON.stringify(statePayload));

    const params = new URLSearchParams({
      response_type: "code",
      client_id: env.VITE_AVELA_STRIPE_OAUTH_CLIENT_ID,
      scope: "read_write",
      state: encodedState,
      redirect_uri: `${window.location.origin}/stripe/oauth/callback`,
    });

    window.location.href = `https://connect.stripe.com/oauth/authorize?${params}`;
  }, [env.VITE_AVELA_STRIPE_OAUTH_CLIENT_ID, organization]);

  const [isDisconnecting, setIsDisconnecting] = React.useState(false);
  const handleDisconnect = React.useCallback(async () => {
    if (!(await confirm())) {
      return;
    }

    const org = organization.toNullable();
    if (!org) {
      console.error("Organization not available for Stripe disconnect");
      return;
    }

    setIsDisconnecting(true);

    try {
      await disconnectStripeAccount({
        path: {
          organizationId: org.id,
        },
        body: {},
      });

      // flush org config cache for payments
      // we wait for 5s before we invalidate org config to allow CDN cache invalidation to finish.
      // it's not 100% guarantee to work all the time, but I think it's the best we can do
      await sleep(5000);
      await invalidateOrgConfig("Payments");

      window.location.reload();
    } catch (err) {
      setIsDisconnecting(false);
      toast.error({
        title: "Failed to disconnect Stripe account",
        description:
          err instanceof Error ? err.message : "An unexpected error occurred",
      });
    }
  }, [confirm, organization, invalidateOrgConfig, toast]);

  const isConnected = status === "connected";

  return (
    <>
      <Flex
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        padding={4}
        alignItems="center"
        gap={4}
        width="100%"
        backgroundColor="white"
      >
        <Image src={stripeIcon} alt="Stripe" boxSize="60px" />

        <Flex direction="column" gap={2} flex={1}>
          <Flex alignItems="center" gap={2}>
            <Text fontWeight="bold" fontSize="lg">
              Stripe
            </Text>
            <ConnectionTag isConnected={isConnected} />
            {isConnected && connectedDate && (
              <Text fontSize="sm" color="gray.500">
                Connected on {connectedDate}
              </Text>
            )}
          </Flex>
          <Text fontSize="sm" color="gray.600">
            {isConnected && stripeAccountId
              ? `Use your own Stripe account: ${stripeAccountId}. `
              : "Use your own Stripe account. You'll be redirected to Stripe to sign in or create a new account. "}
            <Link
              color="blue.500"
              textDecoration="underline"
              href="http://avelahelp.zendesk.com/hc/en-us/articles/47341300235021"
              isExternal
            >
              Learn more
            </Link>
            .
          </Text>
        </Flex>

        {isConnected ? (
          <Flex gap={2} flexShrink={0}>
            <Button
              variant="outline"
              leftIcon={<RiLinkUnlink />}
              onClick={handleDisconnect}
              isLoading={isDisconnecting}
              isDisabled={isDisconnecting}
            >
              Disconnect
            </Button>
            <Button
              as={Link}
              href={stripeUrl}
              isExternal
              variant="outline"
              rightIcon={<ExternalLinkIcon />}
            >
              View in Stripe
            </Button>
          </Flex>
        ) : (
          <Button
            variant="outline"
            rightIcon={<ExternalLinkIcon />}
            onClick={handleConnect}
            flexShrink={0}
            isLoading={isConnecting}
            isDisabled={isConnecting}
          >
            Connect Stripe
          </Button>
        )}
      </Flex>
      {confirmationDialog}
    </>
  );
};
